/**
 * ErrorBoundary.tsx
 *
 * Class-component error boundary used to catch render-time exceptions
 * thrown by descendant components. Without this, an unhandled error in
 * any route (e.g. a Bryntum scheduler crash or a Mapbox GL style failure)
 * blanks the entire admin to a white screen and the operator must hard-
 * reload to recover.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <Routes>...</Routes>
 *   </ErrorBoundary>
 *
 *   <ErrorBoundary fallback={<MapErrorPanel />}>
 *     <MapView {...props} />
 *   </ErrorBoundary>
 */

import React from 'react';
import { withTranslation, type WithTranslation } from 'react-i18next';

interface OwnProps {
  /** Tree to protect. */
  children: React.ReactNode;
  /** Optional custom fallback. When omitted, a localized default UI renders. */
  fallback?: React.ReactNode;
  /** Tag included in console.error output to identify which boundary fired. */
  tag?: string;
}

type Props = OwnProps & WithTranslation;

interface State {
  hasError: boolean;
  error:    Error | null;
}

class ErrorBoundaryInner extends React.Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Log enough to triage in production; the full component stack is in
    // info.componentStack. Wire this to Sentry / your error reporter when
    // available — boundary catches don't auto-report otherwise.
    console.error(`[ErrorBoundary${this.props.tag ? `:${this.props.tag}` : ''}]`, error, info.componentStack);
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;

    const { t } = this.props;
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] p-8 text-center">
        <h2 className="text-2xl font-semibold mb-2">{t('errorBoundary.title')}</h2>
        <p className="text-muted-foreground mb-4 max-w-prose">{t('errorBoundary.description')}</p>
        {this.state.error?.message && (
          <pre className="text-xs text-destructive max-w-prose whitespace-pre-wrap break-words mb-4">
            {this.state.error.message}
          </pre>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={this.reset}
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
          >
            {t('errorBoundary.retry')}
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent"
          >
            {t('errorBoundary.reload')}
          </button>
        </div>
      </div>
    );
  }
}

/**
 * Exported wrapper — the `withTranslation` HOC injects the `t` prop.
 * Explicit type annotation works around a TS2883 inference issue where
 * the HOC's return type references an internal react-i18next helper
 * (`$Subtract`) that isn't part of the public type surface.
 */
export const ErrorBoundary: React.ComponentType<OwnProps> = withTranslation()(ErrorBoundaryInner);
